


import React, { createContext, useReducer, useContext, useEffect } from 'react';
import type { ViewType } from '../types.ts';

// State shape
interface GlobalState {
  activeView: ViewType;
  viewProps: any;
}

// Action types
type Action =
  | { type: 'SET_VIEW'; payload: { view: ViewType, props?: any } };
  // Add other action types as needed

const initialState: GlobalState = {
  activeView: 'ai-command-center',
  viewProps: {},
};

const reducer = (state: GlobalState, action: Action): GlobalState => {
  switch (action.type) {
    case 'SET_VIEW':
      // Reset viewProps on navigation unless new ones are provided
      return { ...state, activeView: action.payload.view, viewProps: action.payload.props || {} };
    default:
      return state;
  }
};

const GlobalStateContext = createContext<{
  state: GlobalState;
  dispatch: React.Dispatch<Action>;
}>({
  state: initialState,
  dispatch: () => null,
});

const LOCAL_STORAGE_KEY = 'devcore_snapshot';

export const GlobalStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState, (initial) => {
        try {
            const storedState = localStorage.getItem(LOCAL_STORAGE_KEY);
            const parsed = storedState ? JSON.parse(storedState) : initial;
             // Ensure a valid initial view, in case stored state is stale
            if (!parsed.activeView) {
                return initial;
            }
            // Don't persist viewProps across sessions
            parsed.viewProps = {};
            return parsed;
        } catch (error) {
            console.error("Failed to parse state from localStorage", error);
            return initial;
        }
    });

    useEffect(() => {
        // Debounce saving to localStorage to avoid performance issues
        const handler = setTimeout(() => {
            try {
                // Don't save transient props to localStorage
                const stateToSave = { ...state, viewProps: {} };
                localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(stateToSave));
            } catch (error) {
                console.error("Failed to save state to localStorage", error);
            }
        }, 500); // Save state 500ms after the last change
        
        return () => clearTimeout(handler);
    }, [state]);

    return (
        <GlobalStateContext.Provider value={{ state, dispatch }}>
            {children}
        </GlobalStateContext.Provider>
    );
};

export const useGlobalState = () => useContext(GlobalStateContext);